const UNKNOWN_FLAG = 'xx';

/**
 * Flag overrides for 2-letter language codes where the flag identifier differs
 * from the language code itself (e.g. `sv` → `se`, `en` → `gb`, `ja` → `jp`).
 * Languages whose flag equals the code (e.g. `de`, `fr`, `it`, `pl`) are omitted
 * and resolved automatically by {@link flagForLanguage}.
 */
const LANGUAGE_FLAG_OVERRIDES: Record<string, string> = {
    aa: 'et',
    ab: 'ge',
    ae: UNKNOWN_FLAG,
    af: 'za',
    ak: 'gh',
    am: 'et',
    an: 'es',
    ar: 'arab',
    as: 'in',
    av: 'ru',
    ay: 'bo',
    ba: 'ru',
    be: 'by',
    bh: 'in',
    bi: 'vu',
    bm: 'ml',
    bn: 'bd',
    bo: 'cn',
    br: 'fr',
    bs: 'ba',
    ca: 'es',
    ce: 'ru',
    ch: 'gu',
    co: 'fr',
    cr: 'ca',
    cs: 'cz',
    cu: UNKNOWN_FLAG,
    cv: 'ru',
    cy: 'gb-wls',
    da: 'dk',
    dv: 'mv',
    dz: 'bt',
    ee: 'gh',
    el: 'gr',
    en: 'gb',
    eo: UNKNOWN_FLAG,
    et: 'ee',
    eu: 'es',
    fa: 'ir',
    ff: 'sn',
    fy: 'nl',
    ga: 'ie',
    gd: 'gb-sct',
    gl: 'es',
    gn: 'py',
    gu: 'in',
    gv: 'im',
    ha: 'ng',
    he: 'il',
    hi: 'in',
    ho: 'pg',
    hy: 'am',
    hz: 'na',
    ia: UNKNOWN_FLAG,
    ie: UNKNOWN_FLAG,
    ig: 'ng',
    ii: 'cn',
    ik: 'us',
    io: UNKNOWN_FLAG,
    iu: 'ca',
    ja: 'jp',
    jv: 'id',
    ka: 'ge',
    kg: 'cd',
    ki: 'ke',
    kj: 'ao',
    kk: 'kz',
    kl: 'gl',
    km: 'kh',
    kn: 'in',
    ko: 'kr',
    kr: 'ng',
    ks: 'in',
    ku: 'iq',
    kv: 'ru',
    kw: 'gb',
    ky: 'kg',
    la: 'va',
    lb: 'lu',
    lg: 'ug',
    li: 'nl',
    ln: 'cd',
    lu: 'cd',
    mi: 'nz',
    ml: 'in',
    mr: 'in',
    ms: 'my',
    my: 'mm',
    na: 'nr',
    nb: 'no',
    nd: 'zw',
    ne: 'np',
    ng: 'na',
    nn: 'no',
    no: 'no',
    nr: 'za',
    nv: 'us',
    ny: 'mw',
    oc: 'fr',
    oj: 'ca',
    om: 'et',
    or: 'in',
    os: 'ru',
    pa: 'in',
    pi: 'in',
    ps: 'af',
    qu: 'pe',
    rm: 'ch',
    rn: 'bi',
    sa: 'in',
    sc: 'it',
    sd: 'pk',
    se: 'no',
    sg: 'cf',
    si: 'lk',
    sl: 'si',
    sm: 'ws',
    sn: 'zw',
    sq: 'al',
    sr: 'rs',
    ss: 'sz',
    st: 'ls',
    su: 'id',
    sv: 'se',
    sw: 'tz',
    ta: 'in',
    te: 'in',
    tg: 'tj',
    ti: 'er',
    tk: 'tm',
    tl: 'ph',
    tn: 'bw',
    ts: 'za',
    tt: 'ru',
    tw: 'gh',
    ty: 'pf',
    ug: 'cn',
    uk: 'ua',
    ur: 'pk',
    ve: 'za',
    vi: 'vn',
    vo: UNKNOWN_FLAG,
    wa: 'be',
    wo: 'sn',
    xh: 'za',
    yi: 'il',
    yo: 'ng',
    za: 'cn',
    zh: 'cn',
    zu: 'za'
};

/**
 * Locale-specific overrides for BCP-47 codes whose flag is not simply the region
 * subtag (e.g. Welsh-in-GB uses the Welsh subdivision flag, not the Union Jack).
 */
const LOCALE_FLAG_OVERRIDES: Record<string, string> = {
    'cy-gb': 'gb-wls',
    'gd-gb': 'gb-sct'
};

/**
 * Resolves the flag-icon identifier for a language code.
 *
 * Resolution order:
 * 1. Full locale override (e.g. `cy-gb` → `gb-wls`).
 * 2. For BCP-47 codes (`xx-YY`), the region subtag (e.g. `de-at` → `at`).
 * 3. Language override (e.g. `sv` → `se`).
 * 4. The language code itself (e.g. `de` → `de`).
 */
export function flagForLanguage(lang: string): string {
    const lower = lang.toLowerCase();
    if (LOCALE_FLAG_OVERRIDES[lower]) {
        return LOCALE_FLAG_OVERRIDES[lower];
    }
    const [language, region] = lower.split('-');
    if (region) {
        return region;
    }
    return LANGUAGE_FLAG_OVERRIDES[language] ?? language;
}
